import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Chip,
  Card,
  CardContent,
  Stack,
  FormControlLabel,
  Switch,
} from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid';
import { useQuery, useMutation } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';
import {
  GET_APP_VERSIONS_LIST,
  GET_APP_VERSION_POLICY,
  CREATE_APP_VERSION,
  UPDATE_APP_VERSION,
  DELETE_APP_VERSION,
  UPDATE_APP_VERSION_POLICY,
} from '@/graphql/billingOps';
import { DataTable } from '@gastrosoft/shared/ui';
import { DeleteConfirmDialog } from '@gastrosoft/shared/ui';
import { useNotification } from '@/app/contexts/NotificationContext';
import { useAuth } from '@/app/auth/AuthContext';
import { isSuperAdmin } from '@/utils/roles';
import { GRAPHQL_MAX_PAGE_SIZE } from '@/utils/constants';

type AppVersionNode = {
  id: string;
  versionName: string;
  versionCode: number;
  releaseNotes: string;
  releaseDate: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
};

type AppVersionsQueryData = {
  appVersions: {
    nodes: AppVersionNode[];
    totalCount: number;
  };
};

type AppVersionPolicyData = {
  appVersionPolicy: {
    requiredMinVersion: string | null;
    suggestedMinVersion: string | null;
    updateStoreUrl: string | null;
  };
};

interface Row extends Record<string, unknown> {
  id: string;
  versionName: string;
  versionCode: number;
  releaseNotes: string;
  releaseDate: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  policyBadges: string;
}

function toDateTimeLocalValue(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fromDateTimeLocalValue(local: string): string {
  const d = new Date(local);
  return Number.isNaN(d.getTime()) ? new Date().toISOString() : d.toISOString();
}

export default function AppVersions() {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === 'de' ? 'de-DE' : 'en-US';
  const formatDate = (iso: string) => new Date(iso).toLocaleString(locale);
  const { user } = useAuth();
  const { showSuccess, showError } = useNotification();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const [createOpen, setCreateOpen] = useState(false);
  const [editRow, setEditRow] = useState<Row | null>(null);
  const [deleteRow, setDeleteRow] = useState<Row | null>(null);

  const [versionName, setVersionName] = useState('');
  const [versionCode, setVersionCode] = useState('');
  const [releaseNotes, setReleaseNotes] = useState('');
  const [releaseDate, setReleaseDate] = useState(toDateTimeLocalValue(new Date().toISOString()));
  const [isActive, setIsActive] = useState(true);

  const [policyRequired, setPolicyRequired] = useState('');
  const [policySuggested, setPolicySuggested] = useState('');
  const [policyStoreUrl, setPolicyStoreUrl] = useState('');

  const { data, loading, refetch } = useQuery<AppVersionsQueryData>(GET_APP_VERSIONS_LIST, {
    variables: { first: GRAPHQL_MAX_PAGE_SIZE },
    skip: !isSuperAdmin(user),
  });

  const { data: policyData, refetch: refetchPolicy } = useQuery<AppVersionPolicyData>(
    GET_APP_VERSION_POLICY,
    {
      skip: !isSuperAdmin(user),
    },
  );

  useEffect(() => {
    const p = policyData?.appVersionPolicy;
    if (!p) return;
    setPolicyRequired(p.requiredMinVersion ?? '');
    setPolicySuggested(p.suggestedMinVersion ?? '');
    setPolicyStoreUrl(p.updateStoreUrl ?? '');
  }, [policyData]);

  const [createAppVersion, { loading: creating }] = useMutation(CREATE_APP_VERSION);
  const [updateAppVersion, { loading: updating }] = useMutation(UPDATE_APP_VERSION);
  const [deleteAppVersion] = useMutation(DELETE_APP_VERSION);
  const [updateAppVersionPolicy, { loading: savingPolicy }] =
    useMutation(UPDATE_APP_VERSION_POLICY);

  const policy = policyData?.appVersionPolicy;

  const versionOptions = useMemo(() => {
    const nodes = data?.appVersions?.nodes ?? [];
    return [...nodes]
      .filter((n) => n.isActive)
      .sort((a, b) => b.versionName.localeCompare(a.versionName, undefined, { numeric: true }))
      .map((n) => n.versionName);
  }, [data]);

  const rows: Row[] = (data?.appVersions?.nodes ?? []).map((n) => ({
    id: n.id,
    versionName: n.versionName,
    versionCode: n.versionCode,
    releaseNotes: n.releaseNotes,
    releaseDate: n.releaseDate,
    isActive: n.isActive,
    createdAt: n.createdAt,
    updatedAt: n.updatedAt,
    policyBadges: '',
  }));

  const columns: GridColDef[] = [
    { field: 'versionName', headerName: t('appVersions.versionName'), flex: 1, minWidth: 120 },
    { field: 'versionCode', headerName: t('appVersions.versionCode'), width: 110 },
    {
      field: 'policyBadges',
      headerName: t('appVersions.policy'),
      width: 200,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const row = params.row as Row;
        const chips: React.ReactNode[] = [];
        if (policy?.requiredMinVersion && row.versionName === policy.requiredMinVersion) {
          chips.push(
            <Chip
              key="req"
              size="small"
              color="error"
              label={t('appVersions.requiredMin')}
              sx={{ mr: 0.5 }}
            />,
          );
        }
        if (policy?.suggestedMinVersion && row.versionName === policy.suggestedMinVersion) {
          chips.push(
            <Chip
              key="sug"
              size="small"
              color="warning"
              label={t('appVersions.suggestedMin')}
              sx={{ mr: 0.5 }}
            />,
          );
        }
        return <>{chips}</>;
      },
    },
    {
      field: 'isActive',
      headerName: t('common.status'),
      width: 100,
      valueFormatter: (v: boolean) => (v ? t('appVersions.active') : t('appVersions.inactive')),
    },
    {
      field: 'releaseDate',
      headerName: t('appVersions.releaseDate'),
      width: 160,
      valueFormatter: (v: string) => formatDate(v),
    },
    {
      field: 'releaseNotes',
      headerName: t('appVersions.releaseNotes'),
      flex: 1,
      minWidth: 160,
      valueFormatter: (v: string) => (v && v.length > 60 ? `${v.slice(0, 60)}…` : v || '–'),
    },
    {
      field: 'updatedAt',
      headerName: t('common.updated'),
      width: 150,
      valueFormatter: (v: string) => formatDate(v),
    },
    {
      field: 'actions',
      headerName: t('common.actions'),
      width: 180,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const row = params.row as Row;
        return (
          <Stack direction="row" spacing={1} sx={{ height: '100%' }}>
            <Button size="small" onClick={() => openEdit(row)}>
              {t('common.edit')}
            </Button>
            <Button size="small" color="error" onClick={() => setDeleteRow(row)}>
              {t('common.delete')}
            </Button>
          </Stack>
        );
      },
    },
  ];

  const resetForm = () => {
    setVersionName('');
    setVersionCode('');
    setReleaseNotes('');
    setReleaseDate(toDateTimeLocalValue(new Date().toISOString()));
    setIsActive(true);
  };

  const openEdit = (row: Row) => {
    setEditRow(row);
    setVersionName(row.versionName);
    setVersionCode(String(row.versionCode));
    setReleaseNotes(row.releaseNotes);
    setReleaseDate(toDateTimeLocalValue(row.releaseDate));
    setIsActive(row.isActive);
  };

  const handleCreate = async () => {
    const vc = parseInt(versionCode, 10);
    if (!versionName.trim() || Number.isNaN(vc)) {
      showError(t('errors.generic'));
      return;
    }
    try {
      const res = await createAppVersion({
        variables: {
          input: {
            versionName: versionName.trim(),
            versionCode: vc,
            releaseNotes,
            releaseDate: fromDateTimeLocalValue(releaseDate),
            isActive,
          },
        },
      });
      const top = (res as { errors?: ReadonlyArray<{ message?: string }> }).errors;
      if (top?.length) {
        showError(top.map((e) => e.message).join(' '));
        return;
      }
      if (
        !(res.data as { createAppVersion?: { appVersion?: unknown } })?.createAppVersion?.appVersion
      ) {
        showError(t('errors.generic'));
        return;
      }
      showSuccess(t('appVersions.created'));
      setCreateOpen(false);
      resetForm();
      await refetch();
      await refetchPolicy();
    } catch (e) {
      showError(e instanceof Error ? e.message : t('errors.generic'));
    }
  };

  const handleUpdate = async () => {
    if (!editRow) return;
    const vc = parseInt(versionCode, 10);
    if (!versionName.trim() || Number.isNaN(vc)) {
      showError(t('errors.generic'));
      return;
    }
    try {
      const res = await updateAppVersion({
        variables: {
          input: {
            id: editRow.id,
            versionName: versionName.trim(),
            versionCode: vc,
            releaseNotes,
            releaseDate: fromDateTimeLocalValue(releaseDate),
            isActive,
          },
        },
      });
      const top = (res as { errors?: ReadonlyArray<{ message?: string }> }).errors;
      if (top?.length) {
        showError(top.map((e) => e.message).join(' '));
        return;
      }
      if (
        !(res.data as { updateAppVersion?: { appVersion?: unknown } })?.updateAppVersion?.appVersion
      ) {
        showError(t('errors.generic'));
        return;
      }
      showSuccess(t('appVersions.updated'));
      setEditRow(null);
      resetForm();
      await refetch();
      await refetchPolicy();
    } catch (e) {
      showError(e instanceof Error ? e.message : t('errors.generic'));
    }
  };

  const handleDelete = async () => {
    if (!deleteRow) return;
    try {
      const res = await deleteAppVersion({
        variables: { input: { id: deleteRow.id } },
      });
      const top = (res as { errors?: ReadonlyArray<{ message?: string }> }).errors;
      if (top?.length) {
        showError(top.map((e) => e.message).join(' '));
        return;
      }
      const ok = (res.data as { deleteAppVersion?: { boolean?: boolean | null } })?.deleteAppVersion
        ?.boolean;
      if (!ok) {
        showError(t('errors.generic'));
        return;
      }
      showSuccess(t('appVersions.deleted'));
      setDeleteRow(null);
      await refetch();
      await refetchPolicy();
    } catch (e) {
      showError(e instanceof Error ? e.message : t('errors.generic'));
    }
  };

  const handleSavePolicy = async () => {
    try {
      const res = await updateAppVersionPolicy({
        variables: {
          input: {
            requiredMinVersion: policyRequired.trim() || null,
            suggestedMinVersion: policySuggested.trim() || null,
            updateStoreUrl: policyStoreUrl.trim() || null,
          },
        },
      });
      const top = (res as { errors?: ReadonlyArray<{ message?: string }> }).errors;
      if (top?.length) {
        showError(top.map((e) => e.message).join(' '));
        return;
      }
      if (
        !(res.data as { updateAppVersionPolicy?: { appVersionPolicy?: unknown } })
          ?.updateAppVersionPolicy?.appVersionPolicy
      ) {
        showError(t('errors.generic'));
        return;
      }
      showSuccess(t('appVersions.policySaved'));
      await refetchPolicy();
      await refetch();
    } catch (e) {
      showError(e instanceof Error ? e.message : t('errors.generic'));
    }
  };

  if (!isSuperAdmin(user)) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="h5">{t('appVersions.title')}</Typography>
        <Typography color="text.secondary" sx={{ mt: 2 }}>
          {t('appVersions.superAdminOnly')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height: 'calc(100vh - 180px)' }}>
      <Typography variant="h4" sx={{ mb: 2 }}>
        {t('appVersions.title')}
      </Typography>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            {t('appVersions.policySection')}
          </Typography>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
            <TextField
              select
              label={t('appVersions.requiredMinVersion')}
              value={policyRequired}
              onChange={(e) => setPolicyRequired(e.target.value)}
              fullWidth
              helperText={t('appVersions.policyHelper')}
            >
              <MenuItem value="">
                <em>{t('appVersions.none')}</em>
              </MenuItem>
              {versionOptions.map((v) => (
                <MenuItem key={v} value={v}>
                  {v}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label={t('appVersions.suggestedMinVersion')}
              value={policySuggested}
              onChange={(e) => setPolicySuggested(e.target.value)}
              fullWidth
            >
              <MenuItem value="">
                <em>{t('appVersions.none')}</em>
              </MenuItem>
              {versionOptions.map((v) => (
                <MenuItem key={v} value={v}>
                  {v}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
          <TextField
            label={t('appVersions.updateStoreUrl')}
            value={policyStoreUrl}
            onChange={(e) => setPolicyStoreUrl(e.target.value)}
            fullWidth
            sx={{ mb: 2 }}
            helperText={t('appVersions.storeUrlHelper')}
          />
          <Button
            variant="contained"
            onClick={() => void handleSavePolicy()}
            disabled={savingPolicy}
          >
            {t('appVersions.savePolicy')}
          </Button>
        </CardContent>
      </Card>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
        <Button
          variant="contained"
          onClick={() => {
            resetForm();
            setCreateOpen(true);
          }}
        >
          {t('appVersions.create')}
        </Button>
      </Box>

      <DataTable<Row>
        enableMobileCards
        columns={columns}
        data={rows}
        loading={loading}
        page={page}
        rowsPerPage={rowsPerPage}
        totalCount={rows.length}
        onPageChange={setPage}
        onRowsPerPageChange={setRowsPerPage}
        paginationMode="client"
      />

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>{t('appVersions.create')}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField
            label={t('appVersions.versionName')}
            value={versionName}
            onChange={(e) => setVersionName(e.target.value)}
            fullWidth
            required
            placeholder="9.1.0"
          />
          <TextField
            label={t('appVersions.versionCode')}
            value={versionCode}
            onChange={(e) => setVersionCode(e.target.value)}
            type="number"
            fullWidth
            required
          />
          <TextField
            label={t('appVersions.releaseDate')}
            type="datetime-local"
            value={releaseDate}
            onChange={(e) => setReleaseDate(e.target.value)}
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
          <FormControlLabel
            control={<Switch checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />}
            label={t('appVersions.active')}
          />
          <TextField
            label={t('appVersions.releaseNotes')}
            value={releaseNotes}
            onChange={(e) => setReleaseNotes(e.target.value)}
            fullWidth
            multiline
            minRows={6}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>{t('common.cancel')}</Button>
          <Button variant="contained" onClick={() => void handleCreate()} disabled={creating}>
            {t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(editRow)} onClose={() => setEditRow(null)} fullWidth maxWidth="md">
        <DialogTitle>{t('appVersions.edit')}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label={t('appVersions.versionName')}
            value={versionName}
            onChange={(e) => setVersionName(e.target.value)}
            fullWidth
            required
            sx={{ mt: 2 }}
          />
          <TextField
            label={t('appVersions.versionCode')}
            value={versionCode}
            onChange={(e) => setVersionCode(e.target.value)}
            type="number"
            fullWidth
            required
          />
          <TextField
            label={t('appVersions.releaseDate')}
            type="datetime-local"
            value={releaseDate}
            onChange={(e) => setReleaseDate(e.target.value)}
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
          <FormControlLabel
            control={<Switch checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />}
            label={t('appVersions.active')}
          />
          <TextField
            label={t('appVersions.releaseNotes')}
            value={releaseNotes}
            onChange={(e) => setReleaseNotes(e.target.value)}
            fullWidth
            multiline
            minRows={6}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditRow(null)}>{t('common.cancel')}</Button>
          <Button variant="contained" onClick={() => void handleUpdate()} disabled={updating}>
            {t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>

      <DeleteConfirmDialog
        open={Boolean(deleteRow)}
        title={t('appVersions.deleteTitle')}
        message={
          deleteRow ? t('appVersions.deleteMessage', { version: deleteRow.versionName }) : ''
        }
        onClose={() => setDeleteRow(null)}
        onConfirm={() => void handleDelete()}
      />
    </Box>
  );
}
